import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { MailWarning, ChevronRight } from "lucide-react";
import apiClient from "../services/api";
import { useAuth } from "../context/AuthContext";

const POLL_MS = 5 * 60 * 1000;

const MailHealthBanner = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const isAdmin = user?.role === "admin";

  const fetchStatus = useCallback(async () => {
    try {
      const res = await apiClient.get(`/oauth/m365-mail/status`);
      setStatus(res.data);
    } catch {
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return undefined;
    fetchStatus();
    const id = setInterval(fetchStatus, POLL_MS);
    const onFocus = () => fetchStatus();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, [isAdmin, fetchStatus, location.pathname]);

  if (!isAdmin || !status || !status.configured || status.healthy) return null;

  const headline = status.connected
    ? "Notification mailbox has a problem — emails may not be going out"
    : "Notification mailbox is not connected — the app cannot send email";

  return (
    <div
      className="bg-red-600 text-white px-4 py-2.5 flex items-center gap-3 text-sm shadow-md"
      role="alert"
      data-testid="mail-health-banner"
    >
      <MailWarning className="w-5 h-5 shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="font-semibold">{headline}</span>
        {status.last_error && (
          <span className="ml-2 text-red-100 truncate hidden md:inline" data-testid="mail-health-banner-error">
            ({status.last_error})
          </span>
        )}
      </div>
      <Link
        to="/settings?tab=backup"
        className="inline-flex items-center gap-1 font-semibold underline underline-offset-2 whitespace-nowrap hover:text-red-100"
        data-testid="mail-health-banner-link"
      >
        Fix in Settings <ChevronRight className="w-4 h-4" />
      </Link>
    </div>
  );
};

export default MailHealthBanner;
